import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { LoggingService } from '../logging/logging.service';
import { environment } from '../../../environments/environment';

/**
 * HTTP Interceptor that:
 * 1. Attaches Auth0 bearer token to API requests
 * 2. Attaches correlation IDs to outgoing requests
 * 3. Logs API errors to Application Insights
 * 4. Normalizes error responses
 */
export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const loggingService = inject(LoggingService);
  const auth0 = inject(Auth0Service);
  const router = inject(Router);

  // Check if this request should include auth token (matches API URL pattern)
  const apiUrl = environment.apiUrl || '';
  const shouldAddToken = apiUrl && req.url.startsWith(apiUrl);

  // Generate correlation ID for this request
  const correlationId = uuidv4();

  // If this is an API request, add the bearer token
  if (shouldAddToken) {
    // Check if this is an anonymous or member login (using our JWT, not Auth0)
    const isAnonymousLogin = localStorage.getItem('anonymous_login') === 'true';
    const isMemberLogin = localStorage.getItem('member_login') === 'true';
    const jwtToken = (isAnonymousLogin || isMemberLogin) ? localStorage.getItem('access_token') : null;

    if (jwtToken) {
      // Use token from localStorage (anonymous or member JWT login)
      const clonedRequest = req.clone({
        setHeaders: {
          'Authorization': `Bearer ${jwtToken}`,
          'X-Correlation-ID': correlationId,
        },
      });

      // Log outgoing request
      loggingService.trackTrace(
        `HTTP ${clonedRequest.method} ${clonedRequest.url}`,
        {
          correlationId,
          method: clonedRequest.method,
          url: clonedRequest.url,
          authType: isAnonymousLogin ? 'anonymous' : 'member',
        }
      );

      return next(clonedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          // Handle 401 Unauthorized (token expired or invalid)
          if (error.status === 401) {
            console.warn('JWT token expired or invalid, clearing session and redirecting to login');
            console.warn('Error details:', error);
            
            // Check WWW-Authenticate header for token expiration details
            const wwwAuth = error.headers.get('WWW-Authenticate');
            if (wwwAuth && wwwAuth.includes('invalid_token')) {
              console.warn('Token expiration detected in WWW-Authenticate header:', wwwAuth);
            }
            
            // Clear the session
            localStorage.removeItem('access_token');
            localStorage.removeItem('anonymous_login');
            localStorage.removeItem('member_login');
            // Redirect to home/login page
            router.navigate(['/'], { queryParams: { sessionExpired: 'true' } });
          }

          // Log error to Application Insights
          console.log('API Error:', error);
          loggingService.trackException(error, {
            correlationId,
            method: clonedRequest.method,
            url: clonedRequest.url,
            status: error.status,
            statusText: error.statusText,
          });

          // Normalize error response
          const normalizedError = {
            message: error.error?.message || error.message || 'An error occurred',
            status: error.status,
            statusText: error.statusText,
            correlationId,
            timestamp: new Date().toISOString(),
          };

          console.error('API Error:', normalizedError);

          return throwError(() => normalizedError);
        })
      );
    }

    // Otherwise, use Auth0 SDK to get token (OAuth login)
    return auth0.getAccessTokenSilently().pipe(
      switchMap(token => {
        // Clone request and add headers        
        const clonedRequest = req.clone({
          setHeaders: {
            'Authorization': `Bearer ${token}`,
            'X-Correlation-ID': correlationId,
          },
        });

        // Log outgoing request
        loggingService.trackTrace(
          `HTTP ${clonedRequest.method} ${clonedRequest.url}`,
          {
            correlationId,
            method: clonedRequest.method,
            url: clonedRequest.url,
            authType: 'oauth',
          }
        );

        return next(clonedRequest).pipe(
          catchError((error: HttpErrorResponse) => {
            // Log error to Application Insights
            console.log('API Error:', error);
            loggingService.trackException(error, {
              correlationId,
              method: clonedRequest.method,
              url: clonedRequest.url,
              status: error.status,
              statusText: error.statusText,
            });

            // Normalize error response
            const normalizedError = {
              message: error.error?.message || error.message || 'An error occurred',
              status: error.status,
              statusText: error.statusText,
              correlationId,
              timestamp: new Date().toISOString(),
            };

            console.error('API Error:', normalizedError);

            return throwError(() => normalizedError);
          })
        );
      }),
      catchError(error => {
        // If token retrieval fails, still proceed with request but log the error
        console.error('Failed to get access token:', error);
        const clonedRequest = req.clone({
          setHeaders: {
            'X-Correlation-ID': correlationId,
          },
        });
        return next(clonedRequest).pipe(
          catchError((httpError: HttpErrorResponse) => {
            loggingService.trackException(httpError, {
              correlationId,
              method: clonedRequest.method,
              url: clonedRequest.url,
              status: httpError.status,
              statusText: httpError.statusText,
            });
            return throwError(() => httpError);
          })
        );
      })
    );
  }

  // Skip adding headers to Auth0 requests (they don't allow custom headers in CORS)
  if (req.url.includes('auth0.com')) {
    return next(req);
  }

  // For non-API requests, just add correlation ID
  const clonedRequest = req.clone({
    setHeaders: {
      'X-Correlation-ID': correlationId,
    },
  });

  // Log outgoing request
  loggingService.trackTrace(
    `HTTP ${clonedRequest.method} ${clonedRequest.url}`,
    {
      correlationId,
      method: clonedRequest.method,
      url: clonedRequest.url,
    }
  );

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // Log error to Application Insights
      loggingService.trackException(error, {
        correlationId,
        method: clonedRequest.method,
        url: clonedRequest.url,
        status: error.status,
        statusText: error.statusText,
      });

      // Normalize error response
      const normalizedError = {
        message: error.error?.message || error.message || 'An error occurred',
        status: error.status,
        statusText: error.statusText,
        correlationId,
        timestamp: new Date().toISOString(),
      };

      console.error('API Error:', normalizedError);

      return throwError(() => normalizedError);
    })
  );
};
